/* aw-cart-sync.js - VARIANT C FORK (2026-07-18, live-test rule 4)
 * Identical to theme B's aw-cart-sync.js except syncOffer(), which now
 * REPLACES the cart's discount-code set via the cart API instead of
 * appending via /discount/{code}. Appending let Shopify keep whichever
 * attached code paid the customer most (home77 -$77 beat family33 -$74.91
 * on 6 jug carts - Brian's 2026-07-18 report + Round 1 finding 6).
 * POST-TEST BACK-PORT CANDIDATE for B: never back-deploy mid-Round-1.
 *
 * Cart-side guardian for the water delivery flow. Three jobs:
 *  1. Keep the refundable bottle deposit quantity locked 1:1 to the water bottle
 *     quantity on ANY cart change. The change listener catches the drawer's
 *     quantity selects; a debounced MutationObserver on [data-products] catches
 *     everything else (remove links, +/- steppers, the theme's own re-renders).
 *     Corrections require a confirmed double-read: the legacy theme's own qty
 *     sequence writes deposits FIRST and water SECOND, so single snapshots lie.
 *  2. Own the drawer's "Renews at" line: published-curve math, shown only while
 *     a subscription line is in the cart. Never the legacy theme.js math
 *     ($24/bottle - the $48/$96/$144 quotes).
 *  3. Keep the auto-applied first-order discount code matched to the cart
 *     composition after cart edits (see syncOffer below).
 *
 * Re-renders go through the theme's own cart:build event so theme.js rebinds its
 * listeners; we never replace [data-products] innerHTML ourselves.
 * Deploy note: Rollouts serving contexts refresh asset digests only on a REAL
 * content change while the experiment is running (identical upserts dedupe;
 * events fired while paused are missed) - hence this line, 2026-07-17.
 */
const DEPOSIT_VARIANT_ID = 50052669866266; // One-Time Refundable Deposit (see theme-launch-admin-checklist.md)
const WATER_HANDLE = 'spring-water-delivery';
const DEPOSIT_HANDLES = ['refundable-jug-deposit', 'one-time-refundable-deposit'];
const DISPENSER_PRODUCT_ID = 8076964419; // 2.5 Gallon Dispenser

let timer = null;
let busy = false;
let dirtyWhileBusy = false; // mutations that land during a pass queue one follow-up
let settleChecked = false; // one bounded follow-up pass per trigger burst

/* Circuit breaker: this module should write to the cart a handful of times per
 * interaction at most. If some interplay with the legacy theme ever loops
 * (write -> re-render -> write), stop writing entirely for the rest of the
 * page view instead of fighting. Checkout enforcement still owns correctness. */
let writeStamps = [];
let tripped = false;
function breakerOk() {
  if (tripped) return false;
  const now = Date.now();
  writeStamps = writeStamps.filter((t) => now - t < 60000);
  if (writeStamps.length >= 10) {
    tripped = true;
    try { console.warn('[aw-cart-sync] write breaker tripped; standing down for this page view'); } catch (_) {}
    return false;
  }
  writeStamps.push(now);
  return true;
}

/* One customer-facing money format ("$104" / "$23.50"): flush dollar sign, two
 * decimals only when there are cents, no USD suffix. Mirrors aw-money.liquid and
 * usd() in aw-order-widget.js; keep the three in sync. (The Subtotal row is
 * deliberately NOT ours: legacy theme.js rewrites it in the shop money format.) */
function usd(cents) {
  const n = cents / 100;
  const digits = n % 1 === 0 ? 0 : 2;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: 2 });
}

function schedule(delay) {
  clearTimeout(timer);
  timer = setTimeout(reconcile, delay);
}

document.addEventListener('change', (e) => {
  if (!e.target.classList || !e.target.classList.contains('water-delivery-cart-select-quantity')) return;
  settleChecked = false;
  schedule(1100); // let the theme's own update land first
});

const observed = new WeakSet();
function observeProducts() {
  document.querySelectorAll('[data-products]').forEach((el) => {
    if (observed.has(el)) return;
    observed.add(el);
    new MutationObserver(() => {
      if (busy) { dirtyWhileBusy = true; return; } // coalesce; drained in reconcile's finally
      settleChecked = false;
      schedule(600);
    }).observe(el, { childList: true, subtree: true });
  });
}

async function reconcile() {
  if (busy) return;
  busy = true;
  try {
    const cart = await fetch('/cart.js').then((r) => r.json());
    updateRenewal(cart);

    let waterQty = 0;
    let hasDispenser = false;
    const depositLines = [];
    for (const item of cart.items) {
      if (item.handle === WATER_HANDLE) waterQty += item.quantity;
      else if (DEPOSIT_HANDLES.some((h) => item.handle.indexOf(h) === 0)) depositLines.push(item);
      if (item.product_id === DISPENSER_PRODUCT_ID) hasDispenser = true;
    }
    syncOffer(cart, waterQty, hasDispenser);
    const primary = depositLines.find((l) => l.variant_id === DEPOSIT_VARIANT_ID) || depositLines[0] || null;

    /* Never correct deposits off a single snapshot. The legacy theme's own
     * quantity sequence edits the DEPOSIT line first and the WATER line second
     * (~1-3s apart), so a mid-sequence read (old water, new deposits) looks
     * like a mismatch that is about to resolve itself - and a write here
     * re-asserts the stale quantity (observed live 2026-07-17: downward edits
     * ended water 4 / deposits 6). Rule: re-read after 1200ms and write only
     * if the water quantity is UNCHANGED across both reads; if water moved,
     * the theme is mid-sequence - stand down and let the settle pass judge
     * the final state. */
    let changed = false;
    const mismatched = depositLines.some((l) => l.quantity !== (l === primary ? waterQty : 0));
    if (mismatched) {
      await new Promise((r) => setTimeout(r, 1200));
      const confirm = await fetch('/cart.js').then((r) => r.json());
      let cWaterQty = 0;
      const cDepositLines = [];
      for (const item of confirm.items) {
        if (item.handle === WATER_HANDLE) cWaterQty += item.quantity;
        else if (DEPOSIT_HANDLES.some((h) => item.handle.indexOf(h) === 0)) cDepositLines.push(item);
      }
      if (cWaterQty === waterQty) {
        const cPrimary = cDepositLines.find((l) => l.variant_id === DEPOSIT_VARIANT_ID) || cDepositLines[0] || null;
        for (const line of cDepositLines) {
          const want = line === cPrimary ? cWaterQty : 0; // one deposit line, matched to jugs
          if (line.quantity !== want && breakerOk()) {
            await change(line.key, want);
            changed = true;
          }
        }
      } else {
        settleChecked = false; // water moved between reads: theme mid-sequence
      }
    }
    if (!primary && waterQty > 0) {
      // "Water present, deposits absent" is also the transient middle of the
      // theme's own remove-water sequence (it zeroes the deposit line first).
      // Confirm with a second read before adding, so we never re-add deposits
      // into a cart that is about to lose its water line.
      await new Promise((r) => setTimeout(r, 1200));
      const confirmCart = await fetch('/cart.js').then((r) => r.json());
      let cWater = 0;
      let cDeposit = 0;
      for (const item of confirmCart.items) {
        if (item.handle === WATER_HANDLE) cWater += item.quantity;
        else if (DEPOSIT_HANDLES.some((h) => item.handle.indexOf(h) === 0)) cDeposit += item.quantity;
      }
      if (cWater > 0 && cDeposit === 0 && breakerOk()) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: DEPOSIT_VARIANT_ID, quantity: cWater }] }),
        });
        changed = true;
      }
    }

    if (changed) {
      // The theme rebuilds its own markup (and rebinds listeners); the rebuild
      // mutates [data-products], which re-runs this reconcile as a final check.
      document.dispatchEvent(new CustomEvent('cart:build'));
      const fresh = await fetch('/cart.js').then((r) => r.json());
      updateRenewal(fresh);
    } else if (!settleChecked) {
      // The theme's own deposit adjustment can land AFTER the last drawer
      // re-render (no DOM signal). One delayed verification pass per burst
      // catches that late write; a second clean pass ends the burst.
      settleChecked = true;
      schedule(2500);
    }
  } catch (_) {
    /* non-blocking; checkout enforcement still owns correctness */
  } finally {
    busy = false;
    if (dirtyWhileBusy) {
      dirtyWhileBusy = false;
      settleChecked = false;
      schedule(600); // re-renders that landed mid-pass get their own pass
    }
    gateButtons();
  }
}

/* Checkout-button gate. The legacy theme ships TWO competing checkDepositAgree
 * implementations: a global one that only reads the DRAWER terms box
 * (#CartTermsDrawer) but fires on every .checkAgreeJS change, and a CartForm one
 * that only reads #DepositAgree but runs only on cart re-renders. Net effect on
 * the /cart page: checking the deposit box DISABLES the button (the global
 * handler consults the hidden drawer's unchecked terms box) until a quantity
 * change forces a re-render (observed live 2026-07-17, arm B day-1 smoke).
 * One rule instead, evaluated per surface and always LAST (microtask after the
 * legacy handlers): a checkout button is enabled iff every agreement checkbox
 * rendered in ITS OWN surface (drawer vs page form) is checked; no checkboxes
 * rendered means nothing to agree to. */
function gateButtons() {
  document.querySelectorAll('button.cart__checkout').forEach((btn) => {
    const scope = btn.closest('#CartDrawer') || btn.closest('form') || document;
    const boxes = Array.from(new Set(scope.querySelectorAll('input.cart__terms-checkbox, input.checkAgreeJS')));
    const ok = boxes.every((b) => b.checked);
    btn.disabled = !ok;
    btn.classList.toggle('cart-checkout-button-disabled', !ok);
  });
}

document.addEventListener('change', (e) => {
  const t = e.target;
  if (!t || !t.matches || !t.matches('input.cart__terms-checkbox, input.checkAgreeJS')) return;
  setTimeout(gateButtons, 0); // after the legacy handlers, so this state wins
});

function change(key, quantity) {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity }),
  });
}

/* Keep the auto-applied first-order discount matched to the cart when
 * quantities change after add to cart. offerCode() mirrors aw-order-widget.js
 * (codes starter22 / home77 / family33; jugs + dispenser count toward the
 * minimums, deposits and shelf never do) - keep the two in sync. Only runs
 * when the widget armed an offer (aw_offer_code in localStorage), so carts
 * built outside the offer flow are never touched. */
/* The only discount codes this theme owns. Anything else attached to the cart
 * belongs to the customer (a typed promo) or a referral/ambassador link
 * (LeadDyno / UpPromote); the cart API's discount field REPLACES the whole code
 * set, so a blind write strips it and kills that order's attribution and the
 * partner's commission. Fix 2026-07-20. */
const THEME_CODES = ['starter22', 'home77', 'family33'];

function offerCode(waterQty, hasDispenser) {
  const eligible = waterQty + (hasDispenser ? 1 : 0);
  if (waterQty < 1) return null;
  if (waterQty <= 3) return eligible >= 3 ? 'starter22' : null;
  if (waterQty <= 5) return hasDispenser && eligible >= 5 ? 'home77' : null;
  return eligible >= 7 ? 'family33' : null;
}

function syncOffer(cart, waterQty, hasDispenser) {
  // Only touch carts the widget armed, same opt-in as B.
  let armed = null;
  let ineligible = null;
  try {
    armed = localStorage.getItem('aw_offer_code');
    ineligible = localStorage.getItem('aw_offer_ineligible');
  } catch (_) { return; }
  if (!armed) return;
  // Eligibility gate. All three theme codes are new-customers-only and
  // one-use-per-customer, so a returning customer must never have one
  // re-attached off a stale aw_offer_code. This file ships on the drawer and is
  // handed no liquid data blob, so it cannot read customerEligible itself; the
  // widget, which can, writes the durable aw_offer_ineligible marker when it
  // suppresses the offer. Contract: marker present = ineligible (stand down
  // completely: do not attach, do not subtract), marker absent = eligible.
  // Fix 2026-07-21.
  if (ineligible) return;
  const want = offerCode(waterQty, hasDispenser) || 'none';
  // C fork: the CART's attached codes are the truth (localStorage lied once:
  // a failed write left it claiming the swap already happened, so every later
  // pass stood down). Compare against cart.discount_codes and re-assert until
  // the cart actually matches; update localStorage only after a 2xx.
  const attached = (cart.discount_codes || []).map((d) => String(d.code || '').toLowerCase());
  // Never clobber a code we did not put there (see THEME_CODES). If the cart
  // carries a customer-entered or referral code, stand down completely: the
  // customer's code wins and their attribution survives.
  if (attached.some((c) => THEME_CODES.indexOf(c) === -1)) return;
  const matches = want === 'none' ? attached.length === 0 : (attached.length === 1 && attached[0] === want);
  if (matches) {
    try { localStorage.setItem('aw_offer_code', want); } catch (_) {}
    return;
  }
  // want === 'none' writes discount:'' on purpose: that is the ONLY path that
  // DETACHES a theme code the cart has stopped qualifying for (drop 8 jugs to 4
  // and family33 must come off, or it rides to checkout and pays out a discount
  // the summary never quoted). Safe because the THEME_CODES check above already
  // stood us down on any cart carrying a code we do not own. Revert 2026-07-21.
  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ discount: want === 'none' ? '' : want }),
  }).then((r) => {
    if (r.ok) { try { localStorage.setItem('aw_offer_code', want); } catch (_) {} }
  }).catch(() => {});
}

/* Renewal line: always the published curve for CA/FL/TX water, on the 4 bottle
 * minimum (trial sizes 1 to 3 continue on the 4 bottle plan). Other regions bill
 * their flat variant price until their curves are published. */
const CURVE = { 4: 2600, 6: 2500, 8: 2400, 10: 2350, 12: 2300 };
function curvePer(qty) {
  if (CURVE[qty]) return CURVE[qty];
  if (qty > 12) return 2300;
  return 2600;
}

function updateRenewal(cart) {
  let total = 0;
  let planName = '';
  for (const item of cart.items) {
    if (!item.selling_plan_allocation) continue;
    if (item.handle === WATER_HANDLE) {
      const eff = Math.max(item.quantity, 4);
      const onCurve = (item.variant_title || '').indexOf('CA/FL/TX') > -1;
      const per = onCurve ? curvePer(eff) : item.selling_plan_allocation.price;
      total += per * eff;
    } else {
      total += item.selling_plan_allocation.price * item.quantity;
    }
    planName = item.selling_plan_allocation.selling_plan.name || planName;
  }
  const show = total > 0;
  if (show) {
    const text = usd(total) + ' ' + planName.replace(/^Deliver every/i, 'every').toLowerCase();
    document.querySelectorAll('[data-aw-recurring]').forEach((el) => { el.textContent = text; });
  }
  document.querySelectorAll('[data-aw-recurring-wrap], [data-aw-recurring-note]').forEach((el) => { el.hidden = !show; });
}

function boot() {
  observeProducts();
  gateButtons();
  fetch('/cart.js').then((r) => r.json()).then(updateRenewal).catch(() => {});
  // Heal any pre-existing deposit mismatch on load - a cart left wrong by an
  // interrupted edit sequence must not survive a page view. Each page view is
  // a fresh breaker budget; reconcile no-ops on clean carts.
  settleChecked = false;
  schedule(1500);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
